import React from 'react';
import { connect } from 'react-redux';
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import { formatAmount, formatDay, formatSignedAmount } from '../../utils';
import { showError, showInfo } from '../../actions/notificationActions';
import Loading from '../Loading';
import {
  ISSUE_OUTCOME,
  NOTES_MAX,
  fetchPaymentNoteHtml,
  isAlreadyIssued,
  isApprovalRequired,
  isUnprocessable,
  isValidationError,
  issueOutcome,
  issuePaymentNote,
  lastCompleteMonth,
  periodLabel,
  periodOptions,
  previewPaymentNote,
  toFieldErrors,
  validateDueDate,
  validateNotes,
  validatePeriod,
} from '../../services/paymentNote.service';
import DataTable from '../DataTable';

// A payment note is a request for payment and a statement of account for the
// overdraft a customer drew over one month. It is deliberately NOT a tax
// invoice — no "invoice", no "INV-", no VAT — and the document says as much in
// its own footer.
//
// The dialog is built around one distinction:
//
//   Preview  — a GET. Nothing is stored, nobody is emailed. Re-run freely, and
//              it is re-run on every change of the period.
//   Issue    — a POST. It persists a snapshot AND emails the customer, once per
//              customer per month, enforced by a database constraint. It cannot
//              be undone, edited, voided or resent.
//
// Because issuing is irreversible and one-shot, it is behind an explicit
// confirmation step that names the amount and the recipient's address. One
// click must never send.
//
// Permission to issue arrives as the `canIssue` prop and is NOT read from
// PermissionContext in here. reactstrap 5.0.0-alpha.4 mounts a Modal with
// `ReactDOM.unstable_renderSubtreeIntoContainer`, i.e. into a separate React
// root rather than a portal, and `createContext` values do not cross that
// boundary — a Consumer inside a Modal gets the context default (`null`) and
// throws. Gating happens in the caller for every modal in this app.

const STATUS_BADGE = {
  draft: { color: 'secondary', label: 'Not yet issued' },
  issued: { color: 'success', label: 'Already issued' },
};

const Figure = ({ label, value, className, hint }) => (
  <Col md={4} xs={6} className="mb-2">
    <small className="text-muted d-block">{label}</small>
    <span className={`font-weight-bold ${className || ''}`}>{value}</span>
    {hint ? <small className="text-muted d-block">{hint}</small> : null}
  </Col>
);

class PaymentNoteModal extends React.Component {
  state = {
    step: 'form', // 'form' -> 'confirm' -> 'result'

    period: lastCompleteMonth(),
    dueAt: '',
    notes: '',

    preview: null,
    previewLoading: false,
    previewError: null, // a message shown in place of the figures

    errors: {},
    formError: null, // server message with no field to attach it to
    submitting: false,
    printing: false,

    result: null, // { outcome, emailed, emailedTo, emailError, message, note }
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.reset();
    }
  }

  reset = () => {
    const period = lastCompleteMonth();

    this.setState(
      {
        step: 'form',
        period,
        dueAt: '',
        notes: '',
        preview: null,
        previewLoading: false,
        previewError: null,
        errors: {},
        formError: null,
        submitting: false,
        printing: false,
        result: null,
      },
      this.loadPreview
    );
  };

  close = () => {
    if (this.state.submitting) return;
    this.props.onClose();
  };

  // The preview has no side effects, so changing the period simply refetches.
  loadPreview = () => {
    const { period } = this.state;
    const periodError = validatePeriod(period);

    if (periodError) {
      this.setState({
        preview: null,
        previewLoading: false,
        previewError: null,
        errors: { period: periodError },
      });
      return;
    }

    this.setState({ previewLoading: true, previewError: null, preview: null });

    previewPaymentNote(this.props.userId, period)
      .then((preview) => {
        // Ignore a response for a period the operator has already moved off.
        if (this.state.period !== period) return;
        this.setState({ previewLoading: false, preview });
      })
      .catch((err) => {
        if (this.state.period !== period) return;

        this.setState({
          previewLoading: false,
          previewError:
            (err && err.message) ||
            'Unable to build a preview for this period.',
        });
      });
  };

  setPeriod = (e) => {
    const period = e.target.value;
    this.setState(
      (state) => ({
        period,
        errors: { ...state.errors, period: undefined },
        formError: null,
      }),
      this.loadPreview
    );
  };

  setInput = (e) => {
    const { name, value } = e.target;
    this.setState((state) => ({
      [name]: value,
      errors: { ...state.errors, [name]: undefined },
      formError: null,
    }));
  };

  /* ---------------------------------------------------------------------- */

  amountDue = () => {
    const { preview } = this.state;
    return preview && preview.summary ? Number(preview.summary.amount_due) : 0;
  };

  recipient = () => {
    const { preview } = this.state;
    return (preview && preview.customer && preview.customer.email) || null;
  };

  alreadyIssued = () => {
    const { preview } = this.state;
    return !!(preview && preview.note && preview.note.status === 'issued');
  };

  // Everything that has to be true before the POST is worth attempting. The
  // server enforces all of it too; this keeps the operator out of a dead end.
  // `explain` marks the reasons worth stating on screen rather than only
  // greying the button out — a "quiet" reason is one the operator can see for
  // themselves (a preview that is still loading) or that already has its own
  // alert (a month that has been issued).
  blockedReason = () => {
    const { preview, previewLoading, previewError } = this.state;

    if (previewLoading) return { message: 'Loading the preview…' };
    if (previewError) return { message: 'The preview could not be loaded.' };
    if (!preview) return { message: 'Choose a month to preview first.' };

    if (this.alreadyIssued()) {
      return {
        message: 'A payment note has already been issued for this month.',
      };
    }
    if (this.amountDue() <= 0) {
      return {
        explain: true,
        message:
          'There is nothing to bill for this month — no overdraft was outstanding, so no payment note can be issued.',
      };
    }
    if (!this.recipient()) {
      return {
        explain: true,
        message:
          'This customer has no email address on file, so there is nobody to send a payment note to. Add an email address to their profile first.',
      };
    }

    return null;
  };

  /* ---------------------------------------------------------------------- */

  // There is no PDF endpoint and we are not building a converter for one. The
  // server renders a self-contained, A4-styled document; handing that markup to
  // a new tab and letting the browser print it is the whole story.
  openOrPrint = () => {
    const { period } = this.state;

    this.setState({ printing: true, formError: null });

    fetchPaymentNoteHtml(this.props.userId, period)
      .then((html) => {
        this.setState({ printing: false });

        const win = window.open('', '_blank');
        if (!win) {
          this.setState({
            formError:
              'Allow pop-ups for this site to open the payment note in a new tab.',
          });
          return;
        }

        win.document.open();
        win.document.write(html);
        win.document.close();

        // No external CSS, fonts or images to wait on, so the document is
        // usually complete the moment it is closed. Guard anyway.
        const print = () => {
          win.focus();
          win.print();
        };
        if (win.document.readyState === 'complete') print();
        else win.onload = print;
      })
      .catch((err) => {
        this.setState({ printing: false });

        if (isUnprocessable(err) || isValidationError(err)) {
          this.setState({
            formError: (err && err.message) || 'This note could not be built.',
          });
          return;
        }

        this.props.dispatch(showError(err));
      });
  };

  review = (e) => {
    if (e) e.preventDefault();

    const errors = {};
    const periodError = validatePeriod(this.state.period);
    const dueError = validateDueDate(this.state.dueAt);
    const notesError = validateNotes(this.state.notes);

    if (periodError) errors.period = periodError;
    if (dueError) errors.due_at = dueError;
    if (notesError) errors.notes = notesError;

    if (Object.keys(errors).length) {
      this.setState({ errors });
      return;
    }

    if (this.blockedReason()) return;

    this.setState({ step: 'confirm', errors: {}, formError: null });
  };

  backToForm = () => {
    if (this.state.submitting) return;
    this.setState({ step: 'form' });
  };

  issue = () => {
    const { userId, onIssued, dispatch } = this.props;
    const { period, dueAt, notes } = this.state;

    this.setState({ submitting: true });

    issuePaymentNote(userId, { period, dueAt, notes })
      .then((res) => {
        const result = issueOutcome(res);
        this.setState({ submitting: false, step: 'result', result });

        // A 201 whose email failed is NOT a success. The note is recorded and
        // the period is now spent, but nobody has been told — so it stays on
        // screen as a warning rather than becoming a green toast.
        if (result.outcome === ISSUE_OUTCOME.SENT) {
          dispatch(
            showInfo(result.message || 'Payment note issued and emailed.')
          );
        }

        if (onIssued) onIssued(result);
      })
      .catch((err) => {
        this.setState({ submitting: false });

        // Maker-checker: nothing was persisted and no email was sent. The
        // interceptor has already opened the approval prompt.
        if (isApprovalRequired(err)) {
          this.props.onClose();
          dispatch(
            showInfo(
              (err && err.message) ||
                'This payment note is pending approval by a second authorised admin.'
            )
          );
          return;
        }

        // 409 — the period is taken. Show the existing note; never retry.
        if (isAlreadyIssued(err)) {
          this.setState(
            {
              step: 'form',
              formError:
                (err && err.message) ||
                'A payment note has already been issued for this period.',
            },
            this.loadPreview // re-reads status: 'issued' and disables issuing
          );
          if (onIssued) onIssued(null);
          return;
        }

        if (isValidationError(err)) {
          const fieldErrors = toFieldErrors(err.errors);
          this.setState({
            step: 'form',
            errors: fieldErrors,
            formError: Object.keys(fieldErrors).length ? null : err.message,
          });
          return;
        }

        // 422 — nothing to bill, or no email address. Operator-actionable.
        if (isUnprocessable(err)) {
          this.setState({
            step: 'form',
            formError:
              (err && err.message) || 'This payment note could not be issued.',
          });
          return;
        }

        dispatch(showError(err));
      });
  };

  /* ---------------------------------------------------------------------- */

  renderPreview() {
    const { preview, previewLoading, previewError } = this.state;

    if (previewLoading) return <Loading />;

    if (previewError) {
      return (
        <div className="text-center" style={{ padding: '16px 0' }}>
          <p className="text-muted mb-2">{previewError}</p>
          <Button color="primary" size="sm" onClick={this.loadPreview}>
            Retry
          </Button>
        </div>
      );
    }

    if (!preview) return null;

    const note = preview.note || {};
    const summary = preview.summary || {};
    const lines = preview.lines || [];
    const badge = STATUS_BADGE[note.status] || STATUS_BADGE.draft;

    return (
      <React.Fragment>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{periodLabel(note.period || this.state.period)}</strong>
          <Badge color={badge.color}>{badge.label}</Badge>
        </div>

        <Row>
          <Figure
            label="Amount requested"
            value={formatAmount(summary.amount_due)}
            className={Number(summary.amount_due) > 0 ? 'text-danger' : ''}
            hint="For this month"
          />
          <Figure
            label="Opening balance"
            value={formatSignedAmount(summary.opening_balance)}
          />
          <Figure
            label="Closing balance"
            value={formatSignedAmount(summary.closing_balance)}
            className={Number(summary.closing_balance) < 0 ? 'text-danger' : ''}
          />
          <Figure
            label="Drawdowns"
            value={formatAmount(summary.total_drawdowns)}
          />
          <Figure
            label="Repayments"
            value={formatAmount(summary.total_repayments)}
          />
          <Figure
            label="Overdraft limit"
            value={formatAmount(summary.overdraft_limit)}
            hint={`${formatAmount(summary.overdraft_available)} available`}
          />
        </Row>

        {/* These figures are period-scoped, not live — the customer's actual
            outstanding debt is the wallet's, shown on the panel behind this
            dialog and in the history table. */}
        <small className="text-muted d-block mb-3">
          Figures cover {note.period_start ? formatDay(note.period_start) : '—'}{' '}
          to {note.period_end ? formatDay(note.period_end) : '—'} only, not the
          live balance.
        </small>

        {lines.length ? (
          <DataTable card={false}>
            <thead className="text-primary">
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Reference</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.reference || i}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {l.date ? formatDay(l.date) : '—'}
                  </td>
                  <td>{l.description}</td>
                  <td>{l.reference || '—'}</td>
                  <td className="text-right">
                    {Number(l.debit) ? formatAmount(l.debit) : '—'}
                  </td>
                  <td className="text-right">
                    {Number(l.credit) ? formatAmount(l.credit) : '—'}
                  </td>
                  <td
                    className={`text-right ${
                      Number(l.balance) < 0 ? 'text-danger' : ''
                    }`}
                  >
                    {formatSignedAmount(l.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : (
          <Alert color="secondary" className="mb-2">
            No overdraft activity in this month — there are no lines to bill.
          </Alert>
        )}

        {!preview.payment ? (
          <Alert color="warning" className="mb-0">
            This customer has no funding account yet, so the note will carry no
            payment details.
          </Alert>
        ) : null}
      </React.Fragment>
    );
  }

  renderForm() {
    const {
      period,
      dueAt,
      notes,
      errors,
      formError,
      printing,
      previewLoading,
    } = this.state;

    const blocked = this.blockedReason();
    const options = periodOptions();

    return (
      <Form onSubmit={this.review}>
        <ModalBody>
          {formError ? <Alert color="danger">{formError}</Alert> : null}

          <Row>
            <Col md={4} xs={12}>
              <FormGroup>
                <Label for="pn-period">Month</Label>
                <Input
                  type="select"
                  name="period"
                  id="pn-period"
                  value={period}
                  onChange={this.setPeriod}
                  valid={errors.period ? false : undefined}
                >
                  {options.map((p) => (
                    <option key={p} value={p}>
                      {periodLabel(p)}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.period}</FormFeedback>
                <FormText color="muted">Complete months only.</FormText>
              </FormGroup>
            </Col>

            <Col md={4} xs={12}>
              <FormGroup>
                <Label for="pn-due">Due date</Label>
                <Input
                  type="date"
                  name="dueAt"
                  id="pn-due"
                  value={dueAt}
                  onChange={this.setInput}
                  valid={errors.due_at ? false : undefined}
                />
                <FormFeedback>{errors.due_at}</FormFeedback>
                <FormText color="muted">
                  Optional — defaults to 14 days from issue.
                </FormText>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="pn-notes">Note to the customer</Label>
            <Input
              type="textarea"
              name="notes"
              id="pn-notes"
              rows={2}
              maxLength={NOTES_MAX}
              placeholder="e.g. Please settle by the due date to keep your facility active."
              value={notes}
              onChange={this.setInput}
              // reactstrap 5.0.0-alpha.4 takes a tri-state `valid`, not the
              // `invalid` prop of later versions — without `valid={false}` the
              // feedback renders but Bootstrap keeps it display: none.
              valid={errors.notes ? false : undefined}
            />
            <FormFeedback>{errors.notes}</FormFeedback>
            <div className="d-flex justify-content-between">
              <small className="text-muted">
                Optional. Appears on the document the customer receives.
              </small>
              <small className="text-muted">
                {(notes || '').length}/{NOTES_MAX}
              </small>
            </div>
          </FormGroup>

          <hr />

          {this.renderPreview()}

          {this.alreadyIssued() ? (
            <Alert color="info" className="mt-3 mb-0">
              A payment note for {periodLabel(period)} has already been issued —
              reference{' '}
              <strong>
                {(this.state.preview.note || {}).reference || '—'}
              </strong>
              . It cannot be issued, edited or resent again. Use{' '}
              <em>Open / print</em> to view it, or find it in the payment note
              history behind this dialog.
            </Alert>
          ) : null}

          {blocked && blocked.explain ? (
            <Alert color="info" className="mt-3 mb-0">
              {blocked.message}
            </Alert>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={this.close}>
            Cancel
          </Button>

          {/* Preview only — no side effects, so this is not permission-gated. */}
          <Button
            outline
            color="secondary"
            onClick={this.openOrPrint}
            disabled={printing || previewLoading || !!this.state.previewError}
          >
            {printing ? 'Preparing…' : 'Open / print'}
          </Button>

          {/* `canIssue` is resolved by the caller, not read from
              PermissionContext here — see the note above the class. */}
          {this.props.canIssue ? (
            <Button
              color="primary"
              type="submit"
              disabled={!!blocked}
              title={blocked ? blocked.message : undefined}
            >
              Issue &amp; email customer…
            </Button>
          ) : null}
        </ModalFooter>
      </Form>
    );
  }

  renderConfirm() {
    const { period, dueAt, notes, submitting } = this.state;
    const amount = this.amountDue();
    const recipient = this.recipient();
    const note = (this.state.preview || {}).note || {};

    return (
      <React.Fragment>
        <ModalBody>
          <p>
            This sends the customer a request for payment. It cannot be undone,
            edited or sent again.
          </p>

          {/* No `borderless` — reactstrap 5.0.0-alpha.4's Table has no such
              prop, so it leaks to the DOM as an invalid attribute and warns. */}
          <DataTable card={false}>
            <tbody>
              <tr>
                <td className="text-muted">Amount requested</td>
                <td>
                  <strong className="text-danger">
                    {formatAmount(amount)}
                  </strong>
                </td>
              </tr>
              <tr>
                <td className="text-muted">Emailed to</td>
                <td>
                  <strong>{recipient}</strong>
                </td>
              </tr>
              <tr>
                <td className="text-muted">Period</td>
                <td>{periodLabel(period)}</td>
              </tr>
              <tr>
                <td className="text-muted">Due</td>
                <td>
                  {dueAt ? (
                    formatDay(dueAt)
                  ) : (
                    <span className="text-muted">14 days from today</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="text-muted">Note</td>
                <td>
                  {notes.trim() ? (
                    notes.trim()
                  ) : (
                    <span className="text-muted">— none —</span>
                  )}
                </td>
              </tr>
            </tbody>
          </DataTable>

          <Alert color="warning" className="mb-0">
            <strong>{formatAmount(amount)}</strong> will be requested from{' '}
            <strong>{recipient}</strong> for {periodLabel(period)}. Only one
            payment note can ever exist per customer per month, so this month
            cannot be re-issued afterwards — there is no edit, void or resend.
          </Alert>

          <small className="text-muted d-block mt-2">
            {note.reference ? `Reference ${note.reference}. ` : ''}
            This may need approval from a second admin before it is sent.
          </small>
        </ModalBody>

        <ModalFooter>
          <Button
            color="secondary"
            onClick={this.backToForm}
            disabled={submitting}
          >
            Back
          </Button>
          <Button color="danger" onClick={this.issue} disabled={submitting}>
            {submitting ? 'Sending…' : `Issue & email ${formatAmount(amount)}`}
          </Button>
        </ModalFooter>
      </React.Fragment>
    );
  }

  renderResult() {
    const { result, period } = this.state;
    if (!result) return null;

    const sent = result.outcome === ISSUE_OUTCOME.SENT;
    const note = result.note || {};

    return (
      <React.Fragment>
        <ModalBody>
          <Alert color={sent ? 'success' : 'warning'} className="mb-3">
            {sent ? (
              <React.Fragment>
                Payment note <strong>{note.reference}</strong> for{' '}
                {periodLabel(note.period || period)} was issued and emailed to{' '}
                <strong>{result.emailedTo}</strong>.
              </React.Fragment>
            ) : (
              <React.Fragment>
                <strong>The payment note was recorded, but not emailed.</strong>
                <div className="mt-2">
                  {note.reference ? (
                    <React.Fragment>
                      Note <strong>{note.reference}</strong> for{' '}
                      {periodLabel(note.period || period)} exists and counts as
                      issued
                      {result.emailedTo ? (
                        <React.Fragment>
                          , but the email to <strong>{result.emailedTo}</strong>{' '}
                          did not go out
                        </React.Fragment>
                      ) : (
                        ', but the customer was not notified'
                      )}
                      .
                    </React.Fragment>
                  ) : (
                    'The customer has not been notified.'
                  )}
                </div>
                {result.emailError ? (
                  <div className="mt-2">
                    <small>Reason: {result.emailError}</small>
                  </div>
                ) : null}
                <div className="mt-2">
                  There is no resend — this month cannot be issued again.{' '}
                  <strong>
                    Contact the customer directly and send them the document
                    yourself
                  </strong>{' '}
                  using <em>Open / print</em> below.
                </div>
              </React.Fragment>
            )}
          </Alert>

          <DataTable card={false}>
            <tbody>
              <tr>
                <td className="text-muted">Reference</td>
                <td>{note.reference || '—'}</td>
              </tr>
              <tr>
                <td className="text-muted">Amount requested</td>
                <td>
                  <strong>{formatAmount(note.amount_due)}</strong>
                </td>
              </tr>
              <tr>
                <td className="text-muted">Due</td>
                <td>{note.due_at ? formatDay(note.due_at) : '—'}</td>
              </tr>
            </tbody>
          </DataTable>

          {result.message ? (
            <small className="text-muted">{result.message}</small>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <Button
            outline
            color="secondary"
            onClick={this.openOrPrint}
            disabled={this.state.printing}
          >
            {this.state.printing ? 'Preparing…' : 'Open / print'}
          </Button>
          <Button color="primary" onClick={this.props.onClose}>
            Done
          </Button>
        </ModalFooter>
      </React.Fragment>
    );
  }

  render() {
    const { isOpen } = this.props;
    const { step } = this.state;

    const title =
      step === 'confirm'
        ? 'Confirm payment note'
        : step === 'result'
        ? 'Payment note issued'
        : 'Generate payment note';

    return (
      <Modal size="lg" isOpen={!!isOpen} toggle={this.close}>
        <ModalHeader toggle={this.close}>{title}</ModalHeader>
        {step === 'confirm'
          ? this.renderConfirm()
          : step === 'result'
          ? this.renderResult()
          : this.renderForm()}
      </Modal>
    );
  }
}

export default connect()(PaymentNoteModal);
